import { HTMLAttributes, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Panel } from "@/components/atoms";
import { selectLastOpenedPanel, setLastOpenedPanel } from "@/redux/slices/quickSetup";
import { clsx } from "@/utils";
import { SetupPanelHeader } from "./SetupPanelHeader";

export interface SetupPanelProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  stepNumber?: number;
  title: string | React.ReactNode;
  description: string;
  stepCount?: number;
  defaultOpen?: boolean;
  isLocked?: boolean;
  completedSteps?: number;
  isFeatured?: boolean;
}

export const SetupPanel = ({
  stepNumber,
  title,
  description,
  stepCount,
  defaultOpen = false,
  isLocked = false,
  completedSteps = 0,
  isFeatured = false,
  children,
  className,
  ...props
}: SetupPanelProps) => {
  const dispatch = useDispatch();
  const lastOpenedPanel = useSelector(selectLastOpenedPanel);

  const titleString = typeof title === "string" ? title : "custom-panel";
  const panelId = `${stepNumber || "custom"}-${titleString.replace(/\s+/g, "-").toLowerCase()}`;

  const [isOpen, setIsOpen] = useState(() => {
    return lastOpenedPanel === panelId || (!lastOpenedPanel && defaultOpen);
  });

  useEffect(() => {
    const shouldBeOpen = lastOpenedPanel === panelId;
    setIsOpen(shouldBeOpen);
  }, [lastOpenedPanel, panelId]);

  const handleToggle = () => {
    if (!isOpen) {
      dispatch(setLastOpenedPanel(panelId));
    } else {
      dispatch(setLastOpenedPanel(null));
    }
    setIsOpen(!isOpen);
  };

  const getFeaturedStyles = () => {
    return isFeatured
      ? "!border-info shadow-focus-ring-info"
      : "!border-vivid-purple shadow-focus-ring-vivid-purple";
  };

  const showProgressBar = stepCount && completedSteps > 0;
  const progressPercentage = stepCount ? (completedSteps / stepCount) * 100 : 0;

  if (isLocked) {
    return (
      <Panel className={clsx("bg-neutral-gray px-5 py-4", className)} {...props}>
        <SetupPanelHeader stepNumber={stepNumber} title={title} description={description} isLocked={true} />
      </Panel>
    );
  }

  return (
    <Panel 
      className={clsx("!p-0 transition-all duration-200", isOpen ? getFeaturedStyles() : "", className)} 
      data-panel-step={stepNumber}
      {...props}
    >
      <SetupPanelHeader
        stepNumber={stepNumber}
        title={title}
        description={description}
        stepCount={stepCount}
        isOpen={isOpen}
        isCompleted={completedSteps === stepCount && stepCount !== 0}
        showProgressBar={!!showProgressBar}
        progressPercentage={progressPercentage}
        onClick={handleToggle}
      />

      <div
        className={clsx("grid transition-[grid-template-rows] duration-200 ease-in-out", isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}
      >
        <div className="overflow-hidden">
          <div
            id={`setup-panel-content-${stepNumber}`}
            className="flex flex-col gap-3 p-4"
            role="region"
            aria-labelledby={`setup-panel-header-${stepNumber}`}
          >
            {children}
          </div>
        </div>
      </div>
    </Panel>
  );
};
